import './index.scss';
import React, { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChartPanel from './components/chart-card';
import ReviewCards from './components/review-cards';
import { COMPONENT_NAME as REVIEW_CARD_COMPONENT_NAME } from './components/review-cards/current-card';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { CM2Dataset, StudySiteDto } from '@interfaces/central-monitoring';
import { MonitoringToolkitContext } from '@pages-clean/central-monitoring/monitoring-toolkit/context';
import ReviewHistoryGrid from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/site-dataset-reviews/components/review-history-grid';
import { useLocalState } from '@hooks/index';
import { useGetDatasetSites, useGetActiveStudyDataSets } from '@apis/bff-api/central-monitoring';
import SiteDatasetReviewsApi from '@apis/bff-api/central-monitoring/site-dataset-reviews';

interface IProps {
    studyRef: string;
    canEdit: boolean;
    datasetId?: number;
}

const COMPONENT_NAMES = [REVIEW_CARD_COMPONENT_NAME];

const SiteDatasetReviews = ({ studyRef, canEdit, datasetId }: IProps) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.siteDatasetReview',
    });

    const localState = useLocalState();

    const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
    const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>(undefined);
    const [selectedDataset, setSelectedDataset] = useState<CM2Dataset | undefined>();
    const [selectedComparisonDataset, setSelectedComparisonDataset] = useState<CM2Dataset | undefined>();

    const { data: sitesData, isLoading: isLoadingSites } = useGetDatasetSites(studyRef, selectedDataset?.id);
    const { data: datasets, isLoading: isLoadingDatasets } = useGetActiveStudyDataSets({ studyId: studyRef });
    const [sites, setSites] = useState<StudySiteDto[] | undefined>([]);

    useEffect(() => {
        if (sitesData) {
            const mappedSites: StudySiteDto[] = sitesData?.toSorted((a, b) =>
                a.siteName.localeCompare(b.siteName, undefined, { numeric: true }),
            );
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSites(mappedSites);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, sitesData);

    const sortedDatasets = (datasets ?? []).map(formatDatasetDates).sort(sortDatasetByDatesDesc);
    const { bottomPanelRef } = use(MonitoringToolkitContext);

    useEffect(() => {
        if (!datasets) return;

        const pageSelectedDataset = localState.state?.selectedCM2Dataset;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (pageSelectedDataset) {
            const matchingDataset =
                sortedDatasets.length > 0 ? sortedDatasets.find((d) => d.id === pageSelectedDataset?.id) : undefined;
            setSelectedDataset(matchingDataset);
        } else
            setSelectedDataset(
                sortedDatasets.length > 0
                    ? (sortedDatasets.find((d) => d.id === datasetId) ?? sortedDatasets[0])
                    : undefined,
            );
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [datasets]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setSelectedSiteId(undefined);
        setSelectedSiteName(undefined);
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [selectedDataset]);

    const { data: previousDatasetIds } = SiteDatasetReviewsApi.useGetPreviousReviewedDatasetIds(
        studyRef,
        selectedSiteId,
        selectedDataset?.id,
    );

    const previousDatasets = sortedDatasets.filter((dataset) => previousDatasetIds?.includes(dataset.id!));

    // If the updated datasets list no longer contains the currently selected dataset, clear down selection
    useEffect(() => {
        if (!previousDatasets.some((dataset) => dataset.id === selectedComparisonDataset?.id)) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSelectedComparisonDataset(undefined);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [previousDatasets]);

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAMES,
    });

    return (
        <div className="site-dataset-reviews">
            <Card
                data-testid="topbar-dropdowns"
                bodyClassName="topbar-content"
                className="topbar-styling topbar-splitters"
            >
                <div className="selector-area">
                    <BasicDropDown
                        label={`${t('selectors.reviewDate.label')}`}
                        monitoringToolkitTopbar
                        data-testid="review-date"
                        data={sortedDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={sortedDatasets.find((d) => d.id === selectedDataset?.id)}
                        onChange={async (e) => {
                            const confirmed = await confirmLossOfChanges();
                            if (!confirmed) {
                                return;
                            }

                            const dataset = e.value as CM2Dataset;
                            setSelectedDataset(dataset);
                        }}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear={false}
                        cardHeader
                        loading={isLoadingDatasets}
                    />
                    <BasicDropDown
                        label={`${t('selectors.site.label')}`}
                        monitoringToolkitTopbar
                        data-testid="site-selector"
                        data={sites ?? []}
                        value={sites?.find((s) => s.id === selectedSiteId) ?? null}
                        dataItemKey="id"
                        textField="siteName"
                        onChange={async (e) => {
                            const confirmed = await confirmLossOfChanges();
                            if (!confirmed) {
                                return;
                            }

                            const site = e.value as StudySiteDto | null;
                            setSelectedSiteId(site?.id);
                            setSelectedSiteName(site?.siteName);
                        }}
                        loading={isLoadingSites}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear
                        cardHeader
                    />
                    <BasicDropDown
                        label={`${t('selectors.lastReviewDate.label')}`}
                        monitoringToolkitTopbar
                        data-testid="last-review-date"
                        data={previousDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={previousDatasets.find((d) => d.id === selectedComparisonDataset?.id) ?? null}
                        onChange={(e) => {
                            const dataset = e.value as CM2Dataset;
                            setSelectedComparisonDataset(dataset);
                        }}
                        disabled={!selectedSiteId}
                        filterable
                        className="dropdown-dataset-picker"
                        customClassName="basic-dropdown-styling"
                        closeWhenThisIsScrolled={bottomPanelRef.current}
                        allowClear
                        cardHeader
                    />
                </div>
            </Card>
            <ReviewCards
                studyRef={studyRef}
                siteId={selectedSiteId}
                dataset={selectedDataset}
                comparisonDataset={selectedComparisonDataset}
                canEdit={canEdit}
            />

            <ChartPanel studyRef={studyRef} selectedSiteId={selectedSiteId} datasetId={selectedDataset?.id} />

            <ReviewHistoryGrid studyRef={studyRef} siteId={selectedSiteId} siteName={selectedSiteName} />
        </div>
    );
};

export default SiteDatasetReviews;
